// navigator.connection is an EventTarget because connection quality updates arrive via its `change` event.
// Declare only the experimental Network Information fields consumed by the diagnostics tracker.
type NetworkInformation = EventTarget & {
    effectiveType?: string
    rtt?: number
}

// Network Information API is optional and intentionally absent from TypeScript's standard Navigator type.
type NavigatorDiagnostics = Navigator & {
    connection?: NetworkInformation
}

export type BrowserStateSnapshot = {
    page: {
        ageMs: number
        visibilityState: DocumentVisibilityState
        visibilityChanges: number
        hiddenDurationMs: number
        lastHiddenAgoMs?: number
    }
    connection: {
        online: boolean
        onlineChanges: number
        lastOfflineAgoMs?: number
        effectiveType?: string
        rttMs?: number
        changes?: number
    }
}

// Subscribe on module load to retain events preceding a reconnect; return no data during static rendering.
export const captureBrowserState: () => BrowserStateSnapshot | undefined =
    typeof window === 'undefined' ? () => undefined : createBrowserStateTracker()

function createBrowserStateTracker(): () => BrowserStateSnapshot {
    const browserNavigator = navigator as NavigatorDiagnostics
    // Wall-clock time includes device sleep and browser suspension, unlike performance.now() on some platforms.
    const startedAt = Date.now()

    let visibilityState = document.visibilityState
    let visibilityChangedAt = startedAt
    let visibilityChanges = 0
    let hiddenDurationMs = 0
    let lastHiddenAt = visibilityState === 'hidden' ? startedAt : undefined

    let onlineChanges = 0
    let lastOfflineAt = navigator.onLine ? undefined : startedAt

    let connectionChanges = 0

    document.addEventListener('visibilitychange', () => {
        const now = Date.now()
        if (visibilityState === 'hidden') {
            hiddenDurationMs += now - visibilityChangedAt
        }

        visibilityState = document.visibilityState
        visibilityChangedAt = now
        visibilityChanges += 1

        if (visibilityState === 'hidden') {
            lastHiddenAt = now
        }
    })

    window.addEventListener('online', () => {
        onlineChanges += 1
    })

    window.addEventListener('offline', () => {
        onlineChanges += 1
        lastOfflineAt = Date.now()
    })

    browserNavigator.connection?.addEventListener('change', () => {
        connectionChanges += 1
    })

    return () => {
        const now = Date.now()
        const connection = browserNavigator.connection

        return {
            page: {
                ageMs: Math.round(now - startedAt),
                visibilityState: document.visibilityState,
                visibilityChanges,
                hiddenDurationMs: Math.round(
                    hiddenDurationMs + (visibilityState === 'hidden' ? now - visibilityChangedAt : 0)
                ),
                lastHiddenAgoMs: elapsed(lastHiddenAt, now),
            },
            connection: {
                // navigator.onLine reports network attachment, not guaranteed Internet reachability.
                online: navigator.onLine,
                onlineChanges,
                lastOfflineAgoMs: elapsed(lastOfflineAt, now),
                effectiveType: connection?.effectiveType,
                rttMs: connection?.rtt,
                changes: connection ? connectionChanges : undefined,
            },
        }
    }
}

function elapsed(since: number | undefined, now: number): number | undefined {
    return since === undefined ? undefined : Math.round(now - since)
}
